import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import {
  EVENT_TYPES_CANON, DAYS_CANON, TIME_RANGES_CANON, BUDGET_MODELS_CANON, DISTRICTS_CANON, FIELD_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} aria-pressed={active} data-testid={testId}
    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'}`}>
    {children}
  </button>
);

const Row = ({ field, missing, children }) => (
  <div data-testid={`complete-field-${field}`}>
    <p className="text-xs font-semibold text-zinc-700 mb-1.5">
      {FIELD_LABELS[field] || field}
      {missing.includes(field) && <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`complete-missing-${field}`}>eksik</span>}
    </p>
    {children}
  </div>
);

// Aynı project_id üzerinde zorunlu alanları tamamlar (PUT /organizer/event-projects/{id}); yeni proje oluşturmaz.
export const ProjectCompletionForm = ({ project, missing = [], onSaved }) => {
  const [form, setForm] = useState({
    title: project.title || '',
    event_type: project.event_type || '',
    expected_attendees: project.expected_attendees ? String(project.expected_attendees) : '',
    preferred_districts: project.preferred_districts || [],
    preferred_days: project.preferred_days || [],
    preferred_time_ranges: project.preferred_time_ranges || [],
    venue_budget_model: project.venue_budget_model || '',
  });
  const [saving, setSaving] = useState(false);
  const toggle = (key, v) => setForm(f => ({ ...f, [key]: f[key].includes(v) ? f[key].filter(x => x !== v) : [...f[key], v] }));

  const save = async () => {
    if (saving) return;
    if (!form.title.trim() || (form.expected_attendees && (!Number.isInteger(Number(form.expected_attendees)) || Number(form.expected_attendees) < 1))) { toast.error('Başlık ve kişi sayısını kontrol et.'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim() || undefined,
        event_type: form.event_type || undefined,
        expected_attendees: form.expected_attendees ? Number(form.expected_attendees) : undefined,
        preferred_districts: form.preferred_districts,
        preferred_days: form.preferred_days,
        preferred_time_ranges: form.preferred_time_ranges,
        venue_budget_model: form.venue_budget_model || undefined,
      };
      const { data } = await axios.put(`${API}/api/organizer/event-projects/${project.id}`, payload);
      toast.success(data.matching?.missing_fields?.length ? 'Kaydedildi — hâlâ eksik alanlar var.' : 'Proje güncellendi.');
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Proje güncellenemedi.');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-zinc-200 bg-[#FAFAFA] p-4 space-y-4" data-testid="project-completion-form">
      <Row field="title" missing={missing}>
        <input aria-label="Proje başlığı" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="complete-title"
          className="w-full h-9 px-3 text-sm rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-[#1E3A8A]" />
      </Row>
      <div className="grid sm:grid-cols-2 gap-4">
        <Row field="event_type" missing={missing}>
          <select aria-label="Etkinlik türü" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))} data-testid="complete-event-type"
            className="w-full h-9 px-2 text-sm rounded-lg border border-zinc-200 bg-white">
            <option value="">Seç…</option>
            {EVENT_TYPES_CANON.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Row>
        <Row field="expected_attendees" missing={missing}>
          <input aria-label="Kişi sayısı" type="number" min="1" value={form.expected_attendees} onChange={e => setForm(f => ({ ...f, expected_attendees: e.target.value }))}
            data-testid="complete-attendees" className="w-full h-9 px-3 text-sm rounded-lg border border-zinc-200 bg-white" />
        </Row>
      </div>
      <Row field="preferred_days" missing={missing}>
        <div className="flex flex-wrap gap-1.5">{DAYS_CANON.map(d => <Chip key={d.value} active={form.preferred_days.includes(d.value)} onClick={() => toggle('preferred_days', d.value)} testId={`complete-day-${d.value}`}>{d.label}</Chip>)}</div>
      </Row>
      <Row field="preferred_time_ranges" missing={missing}>
        <div className="flex flex-wrap gap-1.5">{TIME_RANGES_CANON.map(t => <Chip key={t.value} active={form.preferred_time_ranges.includes(t.value)} onClick={() => toggle('preferred_time_ranges', t.value)} testId={`complete-time-${t.value}`}>{t.label}</Chip>)}</div>
      </Row>
      <Row field="venue_budget_model" missing={missing}>
        <div className="flex flex-wrap gap-1.5">{BUDGET_MODELS_CANON.map(b => <Chip key={b.value} active={form.venue_budget_model === b.value} onClick={() => setForm(f => ({ ...f, venue_budget_model: f.venue_budget_model === b.value ? '' : b.value }))} testId={`complete-budget-${b.value}`}>{b.label}</Chip>)}</div>
      </Row>
      <Row field="preferred_districts" missing={missing}>
        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto border border-zinc-200 rounded-lg p-2 bg-white">
          {DISTRICTS_CANON.map(d => <Chip key={d} active={form.preferred_districts.includes(d)} onClick={() => toggle('preferred_districts', d)} testId={`complete-district-${d}`}>{d}</Chip>)}
        </div>
      </Row>
      <button type="button" onClick={save} disabled={saving} data-testid="project-completion-save"
        className="h-10 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-[#16307A] disabled:opacity-60 transition-colors">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Değişiklikleri kaydet
      </button>
    </div>
  );
};

